/**
 * Vínculo indicador → formulario → preguntas
 * (`/api/indicadores/{id}/formularios`).
 *
 * El formulario de opinión se comparte entre indicadores y cada uno toma de él
 * las preguntas que le corresponden.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http } from '@/services/http'

/** Forma con la que el API de indicador formulario devuelve este dato. */
export interface PreguntaDeFormulario {
  id_pregunta: number
  id_instrumento: number | null
  id_indicador: number | null
  enunciado: string
  tipo_respuesta?: string | null
  orden?: number | null
  /** Indicador al que ya apunta, para no quitársela a otro sin darse cuenta. */
  indicador?: { id_indicador: number; enunciado: string } | null
  grupo_interes?: { id_grupo_interes: number; nombre: string } | null
}

/** Forma con la que el API de indicador formulario devuelve este dato. */
export interface FormularioDisponible {
  id_instrumento: number
  nombre: string
  /** Herramienta: encuesta, grupo focal, taller… */
  tipo?: string | null
  tipo_etiqueta?: string | null
  /** `formulario_completo` va entero al indicador, sin elegir preguntas. */
  modalidad?: 'por_pregunta' | 'formulario_completo' | null
  ya_vinculado?: boolean
  estado?: string | null
  preguntas: PreguntaDeFormulario[]
}

/**
 * Consulta indicador formulario · petición `GET`.
 *
 * @returns FormularioDisponible[]
 */
export async function getFormulariosDisponiblesService(
  idIndicador: number,
  tipo?: string
): Promise<FormularioDisponible[]> {
  const query = tipo ? `?tipo=${encodeURIComponent(tipo)}` : ''
  const r = await http.get<FormularioDisponible[]>(
    `/api/indicadores/${idIndicador}/formularios-disponibles${query}`
  )
  return r.ok ? (r.data ?? []) : []
}

/**
 * Consulta indicador formulario · `GET /api/indicadores`.
 *
 * @returns PreguntaDeFormulario[]
 */
export async function getPreguntasAsignadasService(
  idIndicador: number
): Promise<PreguntaDeFormulario[]> {
  const r = await http.get<PreguntaDeFormulario[]>(`/api/indicadores/${idIndicador}/preguntas-asignadas`)
  return r.ok ? (r.data ?? []) : []
}

/**
 * Vincula el formulario al indicador. Sin `idPreguntas` (formulario completo)
 * el vínculo es del formulario entero y el backend no reparte preguntas.
 */
export async function vincularFormularioService(
  idIndicador: number,
  idInstrumento: number,
  idPreguntas?: number[]
): Promise<{ ok: boolean; mensaje?: string; error?: string }> {
  const r = await http.put<{ message: string }>(`/api/indicadores/${idIndicador}/formulario`, {
    id_instrumento: idInstrumento,
    ...(idPreguntas ? { id_preguntas: idPreguntas } : {})
  })
  if (!r.ok) {
    const primero = Object.values(r.errors ?? {}).flat()[0]
    return { ok: false, error: primero ?? 'No se pudo vincular el formulario.' }
  }
  return { ok: true, mensaje: r.data?.message }
}
